import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../../../state'

/** Identifies this report to the configuration plugin. */
const REPORT_ID = ents.extra.space.Report.Id.ProgressByOrganization

/**
 * Page content for the "Progress by Organization" space report.
 */
@ngCore.Component({
  imports: [
    ds.input.select.single.Component,
    ds.layout.section.Component,
    ds.layout.widget.Component,
  ],
  selector: 'atn-space-report-progress-by-organization',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.reports.Collection} */
  private readonly _reportsEc = ngCore.inject(state.space.reports.Collection)

  /** @see {@link state.space.reports.plugins.configuration.Plugin} */
  public readonly configurationPlugin = this._reportsEc.plugins
    .lookup(state.space.reports.plugins.configuration.Plugin)
    .get()

  /** Configuration settings for this report */
  public readonly configurationSg =
    this.configurationPlugin.selectors.getSettings(REPORT_ID).asSignal

  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)

  /**
   * The organization dimension from the schema
   */
  private readonly _orgDimensionSg = ngCore.computed(
    (schema = this._schemaFeature.selectors.schema.asSignal()) =>
      schema.tryGetDimension(state.space.organizations.constants.DIMENSION_ID),
  )

  /**
   * The organization entities from the organization dimension,
   */
  private readonly _orgEntitiesSg = ngCore.computed(
    (orgDimension = this._orgDimensionSg()) => orgDimension?.entities ?? [],
  )
  /**
   * The items to display in the organization entity configuration dropdown, derived
   * from the organization entities. Each item has an `id` and `name` property,
   * where `id` is the entity ID and `name` is the entity name.
   */
  public readonly orgEntitySelectItems = ngCore.computed<
    readonly ds.input.select.models.Item<ents.core.dms.models.Entity.Id>[]
  >((orgEntities = this._orgEntitiesSg()) =>
    orgEntities.map(({ id: value, name: label }) => ({
      label,
      value,
    })),
  )

  /** Options for the format select. */
  public readonly formatItems: readonly ds.input.select.models.Item<ents.extra.space.Report.Format>[] =
    ents.extra.space.Report.Format.SUPPORTED.map(format => ({
      label: ents.extra.space.Report.Format.getLabel(format),
      value: format,
    }))

  /** Handles a change entity id */
  public entityIdChangeHandler(
    value:
      | ents.extra.space.Report.Configuration.ProgressByOrganization['entityId']
      | undefined,
  ): void {
    if (value) {
      this.configurationPlugin.actions.edit
        .from(
          'SpaceReport.ProgressByOrganization.Configuration.EntityId.Changed',
        )
        .dispatch({ edit: { entityId: value }, id: REPORT_ID })
    }
  }

  /**
   * Handles a change to the format select.
   *
   * NOTE: format is required, so a cleared selection is ignored to keep the
   * last valid value.
   */
  public formatChangeHandler(
    value: ents.extra.space.Report.Format | undefined,
  ): void {
    if (value) {
      this.configurationPlugin.actions.edit
        .from('SpaceReport.ProgressByOrganization.Configuration.Format.Changed')
        .dispatch({ edit: { format: value }, id: REPORT_ID })
    }
  }
}
